import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface Facts {
  name: string;
  where: ReactNode;
  format: string;
  who: string;
  needs: string;
  contents: ReactNode;
}

const code = (text: string) => <span className="api-inline">{text}</span>;

function FactsTable({ items }: { items: Facts[] }) {
  return (
    <div className="dp-fields-table api-field-table-mb">
      {items.map((item) => (
        <div className="dp-field-row" key={item.name}>
          <div className="dp-field-k">{item.name}</div>
          <div className="dp-field-v">
            <div>
              <strong>Where.</strong> {item.where}
            </div>
            <div>
              <strong>Format.</strong> {item.format}
            </div>
            <div>
              <strong>Who.</strong> {item.who}
            </div>
            <div>
              <strong>Needs.</strong> {item.needs}
            </div>
            <div>
              <strong>Contains.</strong> {item.contents}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const DASHBOARD: Facts[] = [
  {
    name: 'MRR movement',
    where: 'Analytics, MRR Movement tab, MRR Movement Breakdown card, Export MRR.',
    format: 'Excel (.xlsx), saved as mrr-movement-YYYY-MM-DD.xlsx. Two sheets: MRR Movement and Summary.',
    who: 'Anyone who can open Analytics. The export query checks sign-in only.',
    needs: 'No tenant flag.',
    contents: 'One row per month in the date range selected on the page: Month, Starting MRR, New Business MRR, Expansion MRR, Reactivation MRR, Contraction MRR, Churn MRR, Net New MRR, Ending MRR, Change %, and the counts of new subscriptions, expansions, contractions, churns and reactivations. The Summary sheet has current and previous month MRR, month and year change, period totals and a generated-at time.',
  },
  {
    name: 'Active subscriptions',
    where: 'Analytics, MRR Movement tab, MRR Movement Breakdown card, Export Subscriptions.',
    format: 'Excel (.xlsx), saved as subscriptions-YYYY-MM-DD.xlsx. Two sheets: Subscriptions and Summary.',
    who: 'Anyone who can open Analytics.',
    needs: 'No tenant flag.',
    contents: 'The subscriptions active today (the page sends no other reference date): Organization, Plan, Variable Pricing, Start Date, End Date, Total Amount, Currency, Period, MRR Contribution, ARR Contribution. The Summary sheet has Total MRR, Total ARR, the active count, the reference date and a generated-at time.',
  },
  {
    name: 'Tax report',
    where: 'Finance, Tax tab, Export CSV or Export Excel.',
    format: 'CSV or Excel, saved as tax-report-START-to-END.csv or .xlsx.',
    who: 'Tenant admins only. The buttons are hidden from other members and the API refuses them.',
    needs: 'The Tax feature (a grantable add-on).',
    contents: 'The report as it is set on screen: period (This month, This quarter, This year or Custom), grouped by Jurisdiction, Tax code or Month, on an Accrual (invoice date) or Cash (as paid) basis. The header rows carry your tax registration numbers. Then Group, Currency, Taxable base, Tax amount and Invoices per row, totals by currency (taxable base, tax charged, tax credited, tax total) and taxable base by treatment. In the CSV, text beginning with = + - or @ is prefixed with an apostrophe so a spreadsheet does not run it.',
  },
  {
    name: 'Form submissions',
    where: 'Forms, open a form, Submissions tab, Export CSV.',
    format: 'CSV with a UTF-8 byte-order mark, saved as FORM-NAME-submissions-YYYY-MM-DD.csv.',
    who: 'Any member with access to Forms.',
    needs: 'The Forms feature.',
    contents: 'Everything the current search and status filter selects, newest first, not just the rows loaded on screen: Submission ID, Submitted at (UTC), Source, Actions, one column per question (including questions removed from later versions), Page URL and Referrer. A file holds at most 10,000 rows. Past that you get the newest 10,000 and a warning.',
  },
  {
    name: 'CRM forecast',
    where: 'Pipeline, Forecast view, Export CSV.',
    format: 'CSV with a byte-order mark, saved as forecast-YYYY-MM-DD.csv.',
    who: 'Anyone who can open Pipeline.',
    needs: 'The CRM feature.',
    contents: 'The deals currently visible in the table, with the columns currently shown, from Company, Contact, Stage, Company domain, Created via, Created on, Created by, Demo, Expected close, First invoice, ARR, Chance to close (%), Plan, Rep, Weighted ARR and Weighted first invoice. A Currency column follows the last money column. The button is disabled when no row is visible.',
  },
  {
    name: 'Import issues',
    where: 'Finance, Import, Transaction Import, Analysis step, Attention Needed, Export CSV (shown only when the analysis flagged something).',
    format: 'CSV, saved as import-issues-YYYY-MM-DD.csv.',
    who: 'Whoever is running the import.',
    needs: 'No tenant flag.',
    contents: 'Row, Issue Type, Message, Client and Amount for each item the analysis flagged.',
  },
  {
    name: 'Users (legacy)',
    where: (
      <>
        Open the command palette (Ctrl+K or Cmd+K), type Export users and choose{' '}
        <strong>Export users…</strong>. It opens {code('/users/export')}. It is in no menu.
      </>
    ),
    format: 'Not a download. The request goes to the external API configured for your workspace, which emails the file to you when it is ready.',
    who: 'Any signed-in member.',
    needs: 'An external API set up under Settings, API Integration. Without it the request fails with “External API is not configured”.',
    contents: 'Users filtered by role and status, using the older role names (Admin, Admin & Coach, Coach, Cofounder, Community Member, Evaluator, Expert, Mentor, Staff, SubAdmin) and statuses (Active, Archived, Deleted, Deny, Pending, Unactive). These are not your Revvly team roles, and the file comes from that external system, not from Revvly.',
  },
];

const FILES: Facts[] = [
  {
    name: 'Invoice PDF',
    where: 'Finance, open an invoice, Details tab, Invoice files, View file, Download. Generate PDF or Regenerate PDF sits under the list.',
    format: 'PDF named invoice-NUMBER.pdf. Regenerating replaces the previous copy.',
    who: 'Any member who can open the invoice. Generating or regenerating is a billing action for workspace admins.',
    needs: 'The Finance feature.',
    contents: 'The invoice as sent to the client. A held draft has no number or dates yet, so it has no PDF until it issues. The same PDF can be attached to a reminder email (Attach invoice PDF), and API clients can read a 30-minute download link, see the API list below.',
  },
  {
    name: 'Invoice attachments',
    where: 'The same Invoice files list. Files you uploaded appear beside the PDF.',
    format: 'The file as uploaded.',
    who: 'Any member who can open the invoice.',
    needs: 'The Finance feature.',
    contents: 'View file opens a preview, and the preview has a Download button.',
  },
  {
    name: 'Library documents',
    where: 'Library, Documents, Download on a document, and on each version in its version history.',
    format: 'The stored file.',
    who: 'Anyone who can open the Library.',
    needs: 'The CRM feature or the Support feature, either one.',
    contents: 'Only files that finished verification can be served.',
  },
];

const API_ONLY: Facts[] = [
  {
    name: 'MRR movement CSV',
    where: <>GraphQL query {code('mrrMovementCsv(months, startMonth, endMonth)')} on the dashboard schema.</>,
    format: 'CSV text returned in the response.',
    who: 'A signed-in session.',
    needs: 'No tenant flag.',
    contents: 'The same monthly movement as the Excel export. Deprecated: use the Excel version. The dashboard does not call it.',
  },
  {
    name: 'Invoice PDF link',
    where: <>The {code('pdfUrl')} field on an invoice in the public GraphQL API.</>,
    format: 'A presigned URL that expires after 30 minutes, minted fresh on every query. Null when no PDF exists.',
    who: 'An API key with the READ capability.',
    needs: 'An API key.',
    contents: 'The same PDF as above. Request it only when you intend to download it. See the Public GraphQL API.',
  },
  {
    name: 'Billed revenue by line kind',
    where: <>GraphQL query {code('billedRevenueByLineKind(from, to, period)')} on the dashboard schema. No screen shows it.</>,
    format: 'JSON rows, not a file.',
    who: 'A signed-in session.',
    needs: 'No tenant flag.',
    contents: 'What was billed per period, by invoice date, split by line kind: BASE or BASE_SPLIT (recurring plan), VARIABLE (usage), FREE_INCLUSION, PRORATION, ONE_TIME_FEE and CREDIT. Imported invoices with no line kind come back with a null kind so totals still reconcile. One row per period, line kind and currency, with amountInReportingCurrency for comparison. The period is daily, weekly or monthly (monthly by default) and the range is at most 1,100 days.',
  },
  {
    name: 'Payment time',
    where: <>GraphQL queries {code('paymentTimeByOrganization')} and {code('paymentTimeByRegion')} on the dashboard schema. No screen shows them.</>,
    format: 'JSON rows, not a file.',
    who: 'A signed-in session.',
    needs: 'No tenant flag.',
    contents: 'For paid invoices: average, fastest and slowest days to payment, the on-time payment rate and the number of invoices paid, per client or per region. The region query names each group by region id.',
  },
  {
    name: 'Form submissions through the Assistant or MCP',
    where: <>The {code('export_form_submissions')} tool, on the Revvly Assistant and the MCP server.</>,
    format: 'The first 100 rows as CSV text, the total row count and the file name.',
    who: 'Whoever the Assistant or MCP session is signed in as. A read tool, so it needs no approval.',
    needs: 'The Forms feature.',
    contents: 'The same filters as the dashboard export. The complete file is downloaded from the Submissions tab.',
  },
];

export function ExportsReference() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Exports and reports — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/reference">Reference</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Exports and reports</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Reference</span>
        <h1>
          Exports and <em>reports</em>.
        </h1>
        <p className="dp-oneliner">
          Every file you can export or download from Revvly, where it lives, its format, who can
          run it and what it needs. Reports that exist only as API queries are marked as such.
        </p>
      </header>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">01</span>
          Dashboard exports
        </h2>
        <FactsTable items={DASHBOARD} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">02</span>
          Files you can download
        </h2>
        <FactsTable items={FILES} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">03</span>
          API and Assistant only
        </h2>
        <p className="dp-section-sub">
          These have no button in the dashboard. The first three are queries on the dashboard
          GraphQL schema, which uses your signed-in session. They are not in the public API for
          API keys.
        </p>
        <FactsTable items={API_ONLY} />
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">04</span>
          Things that look like exports and are not
        </h2>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Subscriptions page, Export</div>
            <div className="dp-field-v">
              The button is on the page, but it has no action behind it today. Use{' '}
              <strong>Export Subscriptions</strong> on Analytics.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Invoice list, Past Due</div>
            <div className="dp-field-v">
              The Finance tabs have no export button. To pull invoices out programmatically, use
              the {code('invoices')} field in the{' '}
              <Link to="/docs/api/graphql-public">Public GraphQL API</Link>.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Contacts and companies import</div>
            <div className="dp-field-v">
              An import, not an export. Its batch history can revert a batch, see{' '}
              <Link to="/docs/guides/fix-your-data">Merge, delete and undo</Link>.
            </div>
          </div>
        </div>
      </section>

      <section className="dp-section">
        <h2>
          <span className="dp-step-num">05</span>
          How spreadsheets are written
        </h2>
        <div className="dp-fields-table api-field-table-mb">
          <div className="dp-field-row">
            <div className="dp-field-k">Excel from the API</div>
            <div className="dp-field-v">
              The Excel queries return the workbook base64-encoded, and the dashboard decodes it
              for you. Decode it yourself when you call them directly.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Formulas in text</div>
            <div className="dp-field-v">
              Tax, form and forecast CSVs prefix any text that starts with = + - or @ with an
              apostrophe, so opening the file never runs a formula typed by a customer.
            </div>
          </div>
          <div className="dp-field-row">
            <div className="dp-field-k">Accented text</div>
            <div className="dp-field-v">
              The form and forecast CSVs begin with a byte-order mark, so Excel reads accents
              correctly.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
